import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import Knowledge from '../../interfaces/Knowledge';
import { isKnowledgeAttachedToAgent } from '../../services/knowledgeService';
import { useKnowledges } from './useKnowledges';

export interface KnowledgeWithAttachment extends Knowledge {
  isAttached: boolean;
  attachmentCount: number;
}

/**
 * Fetches knowledges and checks if each is attached to an agent (call or chat)
 * Used by the Knowledges table
 */
export const useKnowledgesWithAttachment = (
  userId: string | undefined,
  enabled = true
) => {
  // First, get all knowledges using the shared cache
  const {
    data: knowledgesData,
    isLoading: knowledgesLoading,
    error: knowledgesError,
  } = useKnowledges(userId, enabled);

  const knowledges = useMemo(() => {
    return (knowledgesData?.message as Knowledge[]) || [];
  }, [knowledgesData]);

  // Then fetch attachment status for each knowledge
  const attachmentQueries = useQuery<KnowledgeWithAttachment[], Error>({
    queryKey: [
      'knowledges-with-attachment',
      userId,
      knowledges.map(k => k.id).join(','),
    ],
    queryFn: async () => {
      if (!knowledges || knowledges.length === 0) {
        return [];
      }

      // check_knowledge_agent_association checks both call and chat agent
      // containers server-side, so a single call per knowledge is enough.
      const knowledgesWithAttachment = await Promise.all(
        knowledges.map(async knowledge => {
          try {
            const result = await isKnowledgeAttachedToAgent(knowledge.id ?? '');
            const association = result?.message as
              | { attached?: boolean; count?: number }
              | undefined;

            return {
              ...knowledge,
              isAttached: association?.attached ?? false,
              attachmentCount: association?.count ?? 0,
            };
          } catch (error) {
            console.error(
              `Error checking attachment for knowledge ${knowledge.id}:`,
              error
            );
            return {
              ...knowledge,
              isAttached: false,
              attachmentCount: 0,
            };
          }
        })
      );

      return knowledgesWithAttachment;
    },
    enabled: enabled && !!userId && knowledges.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    // Don't set refetchOnMount to false - let React Query refetch stale data
    // This allows cache invalidation to work properly
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    data: attachmentQueries.data || [],
    isLoading: knowledgesLoading || attachmentQueries.isLoading,
    error: knowledgesError || attachmentQueries.error,
  };
};
